/**
 * Environment configuration and validation
 * 
 * Centralizes all environment variable access with type safety
 * and runtime validation.
 */

/**
 * Environment variables configuration
 * All environment variables are accessed through this object
 * to provide type safety and a single source of truth.
 */
export const ENV = {
  /** Current Node environment */
  nodeEnv: process.env.NODE_ENV,
  
  /** Is development mode? */
  isDevelopment: process.env.NODE_ENV === 'development',
  
  /** Is production mode? */
  isProduction: process.env.NODE_ENV === 'production',
  
  /** Is test mode? */
  isTest: process.env.NODE_ENV === 'test',
  
  /** Demo mode for testing without wallet */
  isDemoMode: process.env.REACT_APP_DEMO_MODE === 'true',
  
  /** Google Analytics Tag ID (optional) */
  googleTagId: process.env.REACT_APP_GOOGLE_TAG_ID,
  
  /** Application environment (development, staging, production) */
  appEnvironment: process.env.REACT_APP_ENVIRONMENT || 'development',
  
  /** RPC URL for demo mode */
  rpcUrl: process.env.REACT_APP_RPC_URL,
  
  /** Private key for demo mode (should be in env, not hardcoded) */
  demoPrivateKey: process.env.REACT_APP_DEMO_PRIVATE_KEY,
} as const;

/**
 * Validates required environment variables
 * Call this at app startup to fail fast if configuration is missing
 */
export function validateEnv(): void {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Check for demo mode requirements
  if (ENV.isDemoMode && !ENV.demoPrivateKey) {
    warnings.push(
      'REACT_APP_DEMO_MODE is enabled but REACT_APP_DEMO_PRIVATE_KEY is not set. ' +
      'A hardcoded key will be used (not recommended).'
    );
  }

  // Check for analytics in production
  if (ENV.isProduction && !ENV.googleTagId) {
    warnings.push(
      'REACT_APP_GOOGLE_TAG_ID is not set. Analytics will be disabled in production.'
    );
  }

  // Log warnings
  warnings.forEach(warning => {
    console.warn('⚠️  Environment Warning:', warning);
  });

  // Throw on errors
  if (errors.length > 0) {
    errors.forEach(error => {
      console.error('❌ Environment Error:', error);
    });
    throw new Error(
      `Missing required environment variables. Please check your .env file.\n\n${errors.join('\n')}`
    );
  }
}

/**
 * Checks if we should enable debug features
 * Debug features are only enabled in development or when explicitly requested
 */
export function isDebugEnabled(): boolean {
  return ENV.isDevelopment || process.env.REACT_APP_DEBUG === 'true';
}

/**
 * Example .env file template
 * Create a .env.local file with these variables
 */
export const ENV_TEMPLATE = `
# Node Environment (automatically set)
# NODE_ENV=development

# Demo Mode (for testing without wallet)
REACT_APP_DEMO_MODE=false
REACT_APP_DEMO_PRIVATE_KEY=your_test_private_key_here

# Analytics
REACT_APP_GOOGLE_TAG_ID=G-XXXXXXXXXX

# Application Environment
REACT_APP_ENVIRONMENT=development

# RPC URL for demo mode
REACT_APP_RPC_URL=http://localhost:8545

# Debug mode
REACT_APP_DEBUG=false
`.trim();

